import type { IDBPDatabase } from 'idb';
import {
  AssetNotFoundError,
  type AssetStore,
  type PutAssetInput,
  type StoredAsset,
} from '@burfy/docs-core';
import {
  ASSETS_STORE,
  type BurfyDocsDB,
  DEFAULT_DB_NAME,
  openBurfyDocsDb,
} from './database.ts';

/**
 * Image bytes in IndexedDB, with reference-counted object URLs.
 *
 * The counting is the point. The same image can appear several times in a
 * document, and each view resolves and releases it independently; revoking on
 * the first release would blank every other copy. Never revoking at all would
 * hold the whole document's images in memory for the life of the session.
 */

interface UrlEntry {
  url: string;
  refs: number;
}

export class IndexedDbAssetStore implements AssetStore {
  #db: Promise<IDBPDatabase<BurfyDocsDB>> | null = null;
  readonly #databaseName: string;
  /** Injectable so a test can age an asset without waiting. */
  readonly #now: () => number;
  readonly #urls = new Map<string, UrlEntry>();
  readonly #newId: () => string;

  constructor(
    options: { databaseName?: string; newId?: () => string; now?: () => number } = {},
  ) {
    this.#now = options.now ?? (() => Date.now());
    this.#databaseName = options.databaseName ?? DEFAULT_DB_NAME;
    this.#newId = options.newId ?? (() => crypto.randomUUID());
  }

  /**
   * The database this store is bound to, resolved rather than as passed.
   *
   * Returned resolved so it can be compared with the document store's, which
   * also resolves: the sweep pairs the two, and pairing documents in one
   * database against assets in another finds every asset unreferenced and
   * deletes all of them.
   */
  get databaseName(): string {
    return this.#databaseName;
  }

  #connection(): Promise<IDBPDatabase<BurfyDocsDB>> {
    this.#db ??= openBurfyDocsDb(this.#databaseName);
    return this.#db;
  }

  async close(): Promise<void> {
    for (const entry of this.#urls.values()) URL.revokeObjectURL(entry.url);
    this.#urls.clear();

    const pending = this.#db;
    this.#db = null;
    if (pending) (await pending).close();
  }

  /**
   * The ids of every stored asset.
   *
   * Implementation-specific, like the document store's `listRawRecords`. The
   * port deliberately has no listing — a cloud store would not want one — but
   * the sweep cannot find orphans without knowing what is there.
   */
  async listIds(): Promise<string[]> {
    const db = await this.#connection();
    return (await db.getAllKeys(ASSETS_STORE)).map(String);
  }

  /**
   * When an asset was stored, or 0 for one written before this was recorded.
   *
   * Asked per id rather than by listing every record. The obvious version
   * called `getAll`, which deserializes every image's bytes to read a number
   * — forty 4MB pictures is a 160MB spike on the main thread before first
   * paint, at every start. The sweep only needs the age of the handful of
   * assets nothing refers to, so it asks about those.
   */
  async storedAtOf(id: string): Promise<number> {
    const db = await this.#connection();
    const record = await db.get(ASSETS_STORE, id);
    return record?.storedAt ?? 0;
  }

  async put(input: PutAssetInput): Promise<StoredAsset> {
    const db = await this.#connection();
    const asset: StoredAsset = {
      id: input.id ?? this.#newId(),
      ref: {
        mime: input.mime,
        width: input.width,
        height: input.height,
        byteSize: input.bytes.byteLength,
      },
      bytes: input.bytes,
    };

    // `storedAt` rides alongside rather than inside `StoredAsset`: it is this
    // implementation's bookkeeping for the sweep, not part of the port.
    await db.put(ASSETS_STORE, { ...asset, storedAt: this.#now() });
    return asset;
  }

  async get(id: string): Promise<StoredAsset | null> {
    const db = await this.#connection();
    return (await db.get(ASSETS_STORE, id)) ?? null;
  }

  async resolveUrl(id: string): Promise<string | null> {
    const existing = this.#urls.get(id);
    if (existing) {
      existing.refs += 1;
      return existing.url;
    }

    const asset = await this.get(id);
    if (!asset) return null;

    // Between the lookup and here another caller may have created the URL.
    const raced = this.#urls.get(id);
    if (raced) {
      raced.refs += 1;
      return raced.url;
    }

    const blob = new Blob([asset.bytes as BlobPart], { type: asset.ref.mime });
    const url = URL.createObjectURL(blob);
    this.#urls.set(id, { url, refs: 1 });
    return url;
  }

  release(id: string): void {
    const entry = this.#urls.get(id);
    if (!entry) return;

    entry.refs -= 1;
    if (entry.refs > 0) return;

    URL.revokeObjectURL(entry.url);
    this.#urls.delete(id);
  }

  async remove(id: string): Promise<void> {
    const db = await this.#connection();
    const existing = await db.get(ASSETS_STORE, id);
    if (!existing) throw new AssetNotFoundError(id);

    const entry = this.#urls.get(id);
    if (entry) {
      URL.revokeObjectURL(entry.url);
      this.#urls.delete(id);
    }

    await db.delete(ASSETS_STORE, id);
  }

  /** Live object-URL count, for tests asserting nothing is leaked. */
  get openUrlCount(): number {
    return this.#urls.size;
  }
}
